import React, {useEffect} from 'react';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as RemarkIcon} from '../../../../assets/images/remark-icon.svg';
import {ReactComponent as HighlightingIcon} from '../../../../assets/images/highlighting-icon.svg';
import {ReactComponent as AnnotateIcon} from '../../../../assets/images/annotate-icon.svg';
import {ReactComponent as RedactionIcon} from '../../../../assets/images/annotate-icon.svg';
import {ReactComponent as CopyTextIcon} from '../../../../assets/images/copy-text-icon.svg';
import {ReactComponent as RotateLeftIcon} from '../../../../assets/images/rotate-left-icon.svg';
import {ReactComponent as RotateRightIcon} from '../../../../assets/images/rotate-right-icon.svg';
import {t} from 'i18next';
import {Box} from '@mui/material';
import {ToolbarAction, ToolbarActionType} from './MainPanelToolbar';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {RoleId} from '../../../../shared/constants';
import {MouseToolType} from '../../docViewerUtils';
import {useState} from 'react';
import {ViewerControlTextSelectedEvent} from '../../../../shared/interfaces';

interface PageControllerToolbarProps {
  activeButton: string | null;
  handleToolbarActionChange: (arg0: ToolbarAction) => void;
  viewerControl?: ViewerControl | null;
  isViewerReady?: boolean;
  textSelection?: ViewerControlTextSelectedEvent;
  isTranslations?: boolean;
  isImagesTab?: boolean;
  hasRnfvFiles?: boolean;
}
const PageControllerToolbar: React.FC<PageControllerToolbarProps> = ({
  activeButton,
  handleToolbarActionChange,
  viewerControl,
  isViewerReady,
  textSelection,
  isTranslations = false,
  isImagesTab = false,
  hasRnfvFiles = false,
}) => {
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const activeDocumentObj = useSelector(
    (state: RootState) => state.docView.activeDoc
  );
  const annotationLayerId = useSelector(
    (state: RootState) => state.docView.docViewConfiguration.annotationLayerId
  );
  const [isPanMode, setIsPanMode] = useState(true);

  useEffect(() => {
    if (activeButton === ToolbarActionType.CopyText)
      handleToolbarActionChange({toolbarActionType: ''});
    setIsPanMode(true);
  }, [activeDocumentObj]);

  const clearSelectedText = () => {
    if (!viewerControl?._cs || !isViewerReady) return;
    const sel = textSelection?.textSelection;
    if (!sel) {
      return;
    }
    if (typeof sel.startIndex !== 'number' || !sel.pageReference) {
      return;
    }
    viewerControl.clearMouseSelectedText(sel);
  };

  useEffect(() => {
    if (activeButton !== ToolbarActionType.CopyText) {
      clearSelectedText();
    }
  }, [activeButton]);

  const docViewConfiguration = useSelector(
    (state: RootState) => state.docView.docViewConfiguration
  );
  useEffect(() => {
    viewerControl &&
      isViewerReady &&
      viewerControl._cs && // Check whether _cs is available at runtime
      viewerControl?.fitContent(window.PCCViewer.FitType.FullWidth);
  }, [activeButton, viewerControl, isViewerReady]);

  const handleRotateRight = () => {
    if (viewerControl?._cs) {
      viewerControl.rotatePage(90);
    }
    handleToolbarActionChange({
      toolbarActionType: ToolbarActionType.RotateRight,
    });
  };

  const handleRotateLeft = () => {
    if (viewerControl?._cs) {
      viewerControl.rotatePage(-90);
    }
    handleToolbarActionChange({
      toolbarActionType: ToolbarActionType.RotateLeft,
    });
  };

  const handleRemarkClick = () => {
    if (!viewerControl?._cs) return;

    viewerControl.setCurrentMouseTool(MouseToolType.HighlightAnnotation);
    handleToolbarActionChange({
      toolbarActionType: ToolbarActionType.Remark,
    });
  };

  const handleTextGrab = () => {
    if (!viewerControl?._cs) return;

    if (isPanMode) {
      viewerControl.setCurrentMouseTool(MouseToolType.SelectText);
      handleToolbarActionChange({
        toolbarActionType: ToolbarActionType.CopyText,
      });
    } else {
      clearSelectedText();
      viewerControl.setCurrentMouseTool(MouseToolType.PanAndEdit);
      handleToolbarActionChange({toolbarActionType: ''});
    }

    setIsPanMode((prev) => !prev);
  };

  const showMarkControl = Boolean(
    loginUserData.roleId.toString() != RoleId.ProjectAdministrator &&
    Boolean(Number(annotationLayerId))
  );

  return (
    <Box className="page-controller-toolbar">
      <ArrIconButton
        icon={<RotateRightIcon />}
        onClick={handleRotateRight}
        ariaLabel="rotate-right"
        shape="none"
        size="small"
        tooltipTrigger="hover"
        tooltipContent={t('docView.toolbar.rotateRight')}
        tooltipFocusableWrapper={true}
        data-testid="rotate-right-button"
        className={`rotate-icon action-icon ${
          activeButton == ToolbarActionType.RotateRight ? 'pressed' : ''
        }`}
        testId="page-controller-toolbar-rotate-right-icon-button"
      />
      <ArrIconButton
        icon={<RotateLeftIcon />}
        onClick={handleRotateLeft}
        ariaLabel="rotate-left"
        shape="none"
        size="small"
        tooltipTrigger="hover"
        tooltipContent={t('docView.toolbar.rotateLeft')}
        tooltipFocusableWrapper={true}
        data-testid="rotate-left-button"
        className={`rotate-icon action-icon ${activeButton == ToolbarActionType.RotateLeft ? 'pressed' : ''}`}
        testId="page-controller-toolbar-rotate-left-icon-button"
      />
      <ArrIconButton
        icon={<CopyTextIcon />}
        onClick={handleTextGrab}
        ariaLabel="copy-text"
        shape="none"
        size="small"
        tooltipTrigger="hover"
        tooltipContent={t('docView.toolbar.selectText')}
        tooltipFocusableWrapper={true}
        data-testid="copy-text-button"
        className={`copy-text-icon action-icon ${activeButton == ToolbarActionType.CopyText ? 'pressed' : ''}`}
        testId="page-controller-toolbar-copy-text-icon-button"
      />
      {showMarkControl &&
        docViewConfiguration.imageRedactions &&
        !isTranslations &&
        !isImagesTab && (
          <ArrIconButton
            icon={<RedactionIcon />}
            onClick={() =>
              handleToolbarActionChange({
                toolbarActionType: ToolbarActionType.Redact,
              })
            }
            ariaLabel="redact"
            shape="none"
            size="small"
            tooltipTrigger="hover"
            tooltipContent={
              hasRnfvFiles
                ? t('docView.toolbar.rnfvRestrictions.redact')
                : t('docView.toolbar.redact')
            }
            tooltipFocusableWrapper={true}
            disabled={hasRnfvFiles}
            data-testid="redact-button"
            className={`redact-icon action-icon ${activeButton == ToolbarActionType.Redact ? 'pressed' : ''}`}
            testId="page-controller-toolbar-redact-icon-button"
          />
        )}
      {showMarkControl &&
        docViewConfiguration.isEnableHighlighting &&
        !isTranslations &&
        !isImagesTab && (
          <ArrIconButton
            icon={<AnnotateIcon />}
            onClick={() =>
              handleToolbarActionChange({
                toolbarActionType: ToolbarActionType.Annotate,
              })
            }
            ariaLabel="annotate"
            shape="none"
            size="small"
            tooltipTrigger="hover"
            tooltipContent={t('docView.toolbar.annotate')}
            tooltipFocusableWrapper={true}
            data-testid="annotate-button"
            className={`annotate-icon action-icon ${activeButton == ToolbarActionType.Annotate ? 'pressed' : ''}`}
            testId="page-controller-toolbar-annotate-icon-button"
          />
        )}
      {!isTranslations && !isImagesTab && (
        <ArrIconButton
          icon={<HighlightingIcon />}
          onClick={() =>
            handleToolbarActionChange({
              toolbarActionType: ToolbarActionType.PersistetHighlight,
            })
          }
          ariaLabel="persistent-highlight"
          shape="none"
          size="small"
          tooltipTrigger="hover"
          tooltipContent={t('docView.toolbar.persistentHighlighting')}
          tooltipFocusableWrapper={true}
          data-testid="highlight-button"
          className={`highlight-icon action-icon ${activeButton == ToolbarActionType.PersistetHighlight ? 'pressed' : ''}`}
          testId="page-controller-toolbar-persistent-highlight-icon-button"
        />
      )}
      {showMarkControl &&
        docViewConfiguration.reviewerRemarks &&
        !isTranslations &&
        !isImagesTab && (
          <ArrIconButton
            icon={<RemarkIcon />}
            onClick={handleRemarkClick}
            ariaLabel="remark"
            shape="none"
            size="small"
            tooltipTrigger="hover"
            tooltipContent={t('docView.toolbar.reviewerRemarks')}
            tooltipFocusableWrapper={true}
            data-testid="remark-button"
            className={`remark-icon action-icon ${activeButton == ToolbarActionType.Remark ? 'pressed' : ''}`}
            testId="page-controller-toolbar-remark-icon-button"
          />
        )}
    </Box>
  );
};

export default PageControllerToolbar;
